import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  DollarSign,
  Clock,
  CheckCircle,
  Search,
  Eye,
  ChevronDown,
  ChevronUp,
  Download,
  BarChart3,
  Wallet,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";
import ViewPayoutRequestModal from "../../components/ViewPayoutRequestModal";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import MarkAsPaidModal from "../../components/MarkAsPaidModal";
import Navbar from "../../components/Navbar";

const PayoutRequests = () => {
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [deletingRequest, setDeletingRequest] = useState(null);
  const [markingAsPaidRequest, setMarkingAsPaidRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    totalAmount: 0,
    pendingAmount: 0,
    paidAmount: 0,
    averageAmount: 0,
  });

  // Mock data - replace with actual API calls
  const mockPayoutRequests = [
    {
      id: 1,
      promoterId: 1,
      promoterName: "John Doe",
      promoterEmail: "john.doe@example.com",
      promoterPhone: "+233201234567",
      productName: "iPhone 15 Pro Max",
      productId: "PROD001",
      amount: 540.0,
      status: "pending",
      requestDate: "2024-01-20",
      processedDate: null,
      payoutInfo: {
        network: "mtn",
        phoneNumber: "+233201234567",
        accountName: "John Doe",
      },
      notes: "First payout request for this product",
      adminNotes: "",
      trackingCode: "AFF12345",
      commissionRate: 15,
      originalPrice: 1199.99,
      discountedPrice: 999.99,
      savings: 200.0,
      clicks: 234,
      conversions: 12,
      conversionRate: "5.1%",
      affiliateLink:
        "https://dealboxx.com/deal/iphone-15-pro-max?ref=promoter123",
    },
    {
      id: 2,
      promoterId: 2,
      promoterName: "Sarah Johnson",
      promoterEmail: "sarah.j@example.com",
      promoterPhone: "+233207654321",
      productName: "Samsung Galaxy S24 Ultra",
      productId: "PROD002",
      amount: 304.0,
      status: "paid",
      requestDate: "2024-01-19",
      processedDate: "2024-01-20",
      payoutInfo: {
        network: "vodafone",
        phoneNumber: "+233207654321",
        accountName: "Sarah Johnson",
      },
      notes: "Regular payout request",
      adminNotes: "Paid successfully",
      trackingCode: "AFF12346",
      commissionRate: 12,
      originalPrice: 1299.99,
      discountedPrice: 1099.99,
      savings: 200.0,
      clicks: 189,
      conversions: 8,
      conversionRate: "4.2%",
      affiliateLink:
        "https://dealboxx.com/deal/samsung-s24-ultra?ref=promoter123",
    },
    {
      id: 3,
      promoterId: 1,
      promoterName: "John Doe",
      promoterEmail: "john.doe@example.com",
      promoterPhone: "+233201234567",
      productName: "MacBook Air M2",
      productId: "PROD003",
      amount: 330.0,
      status: "pending",
      requestDate: "2024-01-18",
      processedDate: null,
      payoutInfo: {
        network: "mtn",
        phoneNumber: "+233201234567",
        accountName: "John Doe",
      },
      notes: "Second payout request from this promoter",
      adminNotes: "",
      trackingCode: "AFF12347",
      commissionRate: 15,
      originalPrice: 1099.99,
      discountedPrice: 899.99,
      savings: 200.0,
      clicks: 156,
      conversions: 6,
      conversionRate: "3.8%",
      affiliateLink: "https://dealboxx.com/deal/macbook-air-m2?ref=promoter123",
    },
    {
      id: 4,
      promoterId: 5,
      promoterName: "David Brown",
      promoterEmail: "david.brown@example.com",
      promoterPhone: "+233204445556",
      productName: "AirPods Pro",
      productId: "PROD004",
      amount: 112.0,
      status: "pending",
      requestDate: "2024-01-17",
      processedDate: null,
      payoutInfo: {
        network: "mtn",
        phoneNumber: "+233204445556",
        accountName: "David Brown",
      },
      notes: "Request for audio product payout",
      adminNotes: "",
      trackingCode: "AFF12348",
      commissionRate: 18,
      originalPrice: 249.99,
      discountedPrice: 199.99,
      savings: 50.0,
      clicks: 98,
      conversions: 4,
      conversionRate: "4.1%",
      affiliateLink: "https://dealboxx.com/deal/airpods-pro?ref=promoter123",
    },
    {
      id: 5,
      promoterId: 2,
      promoterName: "Sarah Johnson",
      promoterEmail: "sarah.j@example.com",
      promoterPhone: "+233207654321",
      productName: "Nike Air Max 270",
      productId: "PROD005",
      amount: 105.0,
      status: "pending",
      requestDate: "2024-01-16",
      processedDate: null,
      payoutInfo: {
        network: "vodafone",
        phoneNumber: "+233207654321",
        accountName: "Sarah Johnson",
      },
      notes: "Fashion product payout request",
      adminNotes: "",
      trackingCode: "AFF12349",
      commissionRate: 12,
      originalPrice: 150.0,
      discountedPrice: 120.0,
      savings: 30.0,
      clicks: 145,
      conversions: 7,
      conversionRate: "4.8%",
      affiliateLink:
        "https://dealboxx.com/deal/nike-air-max-270?ref=promoter123",
    },
  ];

  useEffect(() => {
    // Simulate API call
    setIsLoading(true);
    setTimeout(() => {
      setPayoutRequests(mockPayoutRequests);
      setFilteredRequests(mockPayoutRequests);
      calculateStats(mockPayoutRequests);
      setIsLoading(false);
    }, 1000);
  }, []);

  const calculateStats = (requestsList) => {
    const stats = {
      total: requestsList.length,
      pending: requestsList.filter((r) => r.status === "pending").length,
      paid: requestsList.filter((r) => r.status === "paid").length,
      totalAmount: requestsList.reduce((sum, r) => sum + r.amount, 0),
      pendingAmount: requestsList
        .filter((r) => r.status === "pending")
        .reduce((sum, r) => sum + r.amount, 0),
      paidAmount: requestsList
        .filter((r) => r.status === "paid")
        .reduce((sum, r) => sum + r.amount, 0),
      averageAmount:
        requestsList.length > 0
          ? requestsList.reduce((sum, r) => sum + r.amount, 0) /
            requestsList.length
          : 0,
    };
    setStats(stats);
  };

  const filterAndSortRequests = () => {
    let filtered = payoutRequests.filter((request) => {
      const matchesSearch =
        request.promoterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.trackingCode.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || request.status === filterStatus;
      return matchesSearch && matchesStatus;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "date":
          comparison = new Date(b.requestDate) - new Date(a.requestDate);
          break;
        case "amount":
          comparison = b.amount - a.amount;
          break;
        case "promoter":
          comparison = a.promoterName.localeCompare(b.promoterName);
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        case "product":
          comparison = a.productName.localeCompare(b.productName);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === "desc" ? comparison : -comparison;
    });

    setFilteredRequests(filtered);
  };

  useEffect(() => {
    filterAndSortRequests();
  }, [payoutRequests, searchTerm, filterStatus, sortBy, sortOrder]);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "paid":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "paid":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleStatusChange = (requestId, newStatus, adminNotes = "") => {
    setPayoutRequests((prev) =>
      prev.map((r) =>
        r.id === requestId
          ? {
              ...r,
              status: newStatus,
              processedDate:
                newStatus !== "pending"
                  ? new Date().toISOString().split("T")[0]
                  : null,
              adminNotes: adminNotes || r.adminNotes,
            }
          : r
      )
    );

    const statusText = newStatus === "paid" ? "marked as paid" : "updated";
    toast.success(`Payout request ${statusText} successfully`);
  };

  return (
    <>
      <Navbar page={"Payout Requests"} />
      <div className="p-6 min-h-screen bg-gray-50">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Payout Requests
              </h1>
              <p className="mt-2 text-gray-600">
                Manage and process promoter payout requests
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  /* Export functionality */
                }}
                className="flex gap-2 items-center px-4 py-2 text-white bg-gray-600 rounded-lg transition-colors hover:bg-gray-700"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Total Requests</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.total}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Pending Requests</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {stats.pending}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Paid Requests</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.paid}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Pending Amount</p>
                  <p className="text-2xl font-bold text-gray-900">
                    GH₵{stats.pendingAmount.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Wallet className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Average Amount</p>
                  <p className="text-2xl font-bold text-green-600">
                    GH₵{stats.averageAmount.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="p-6 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="flex flex-col gap-4 justify-between items-start lg:flex-row lg:items-center">
            <div className="flex flex-col flex-1 gap-3 sm:flex-row">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent w-full sm:w-64"
                />
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
              >
                <option value="date">Sort by Date</option>
                <option value="amount">Sort by Amount</option>
                <option value="promoter">Sort by Promoter</option>
                <option value="status">Sort by Status</option>
                <option value="product">Sort by Product</option>
              </select>

              <button
                onClick={() =>
                  setSortOrder(sortOrder === "desc" ? "asc" : "desc")
                }
                className="flex gap-1 items-center px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                <span className="hidden sm:inline">
                  {sortOrder === "desc" ? "Sort Down" : "Sort Up"}
                </span>
                {sortOrder === "desc" ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Payout Requests Table */}
        <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Promoter
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Product
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Request Date
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Tracking Code
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6C6C]"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No payout requests found matching your criteria
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <motion.tr
                      key={request.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="transition-colors hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] flex items-center justify-center">
                              <span className="text-sm font-semibold text-white">
                                {request.promoterName
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {request.promoterName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.promoterEmail}
                            </div>
                            <div className="text-xs text-gray-400">
                              {request.promoterPhone}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {request.productName}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {request.productId}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-bold text-[#FF6C6C]">
                            GH₵{request.amount.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {request.commissionRate}% commission
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2 items-center">
                          {getStatusIcon(request.status)}
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              request.status
                            )}`}
                          >
                            {request.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(request.requestDate).toLocaleDateString()}
                        {request.processedDate && (
                          <div className="text-xs text-gray-500">
                            Processed:{" "}
                            {new Date(
                              request.processedDate
                            ).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-mono text-sm text-gray-900">
                          {request.trackingCode}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              setViewingRequest(request);
                              setShowViewModal(true);
                            }}
                            className="text-[#FF6C6C] hover:text-[#ff5a5a]"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {request.status === "pending" && (
                            <button
                              onClick={() => {
                                setMarkingAsPaidRequest(request);
                                setShowMarkAsPaidModal(true);
                              }}
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {request.status === "pending" && (
                            <button
                              onClick={() => {
                                setDeletingRequest(request);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* View Request Modal */}
        {showViewModal && viewingRequest && (
          <ViewPayoutRequestModal
            request={viewingRequest}
            onClose={() => {
              setShowViewModal(false);
              setViewingRequest(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingRequest(null);
          }}
          onConfirm={() => {
            if (deletingRequest) {
              setPayoutRequests((prev) =>
                prev.filter((r) => r.id !== deletingRequest.id)
              );
              setShowDeleteModal(false);
              setDeletingRequest(null);
              toast.success(
                `Payout request for ${deletingRequest.productName} deleted successfully`
              );
            }
          }}
          title="Delete Payout Request"
          message="Are you sure you want to delete this payout request?"
          itemName={
            deletingRequest
              ? `${deletingRequest.productName} - ${deletingRequest.promoterName}`
              : ""
          }
          itemType="payout request"
        />

        {/* Mark as Paid Modal */}
        <MarkAsPaidModal
          isOpen={showMarkAsPaidModal}
          onClose={() => {
            setShowMarkAsPaidModal(false);
            setMarkingAsPaidRequest(null);
          }}
          onConfirm={(requestId, status, adminNotes) => {
            handleStatusChange(requestId, status, adminNotes);
            setShowMarkAsPaidModal(false);
            setMarkingAsPaidRequest(null);
          }}
          request={markingAsPaidRequest}
        />
      </div>
    </>
  );
};

export default PayoutRequests;
